/**
 * @fileoverview Componente responsável por sinalizar o status atual dos recursos de IA.
 */

import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  // Variante compact: inline para TicketListItem
  compact: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    animation: "$fadeIn 0.3s ease-out",
  },
  dotsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    animation: "$pulse 1.4s infinite ease-in-out",
    "&:nth-child(1)": { animationDelay: "0s" },
    "&:nth-child(2)": { animationDelay: "0.2s" },
    "&:nth-child(3)": { animationDelay: "0.4s" },
  },
  text: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  "@keyframes pulse": {
    "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
    "40%": { transform: "scale(1)", opacity: 1 },
  },
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

const STATUS_MAP = {
  thinking: "Pensando...",
  listening: "Ouvindo áudio...",
  viewing: "Analisando imagem...",
  remembering: "Consultando memória...",
  searching: "Gerando resposta...",
  responding: "Enviando...",
  waiting_approval: "Aguardando aprovação...",
};

const AiStatusIndicator = ({ ticketId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [status, setStatus] = useState(null);
  const [agentName, setAgentName] = useState("");
  const socketManager = useContext(SocketContext);
  const timerRef = useRef(null);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !ticketId) return;

    const socket = socketManager.getSocket(companyId);

    const handleAiStatus = (data) => {
      if (String(data.ticketId) !== String(ticketId)) return;

      if (data.status === "done" || data.status === "error") {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setStatus(null);
          setAgentName("");
        }, 500);
        return;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      setStatus(data.status);
      setAgentName(data.agentName || "IA");

      // Safety timeout 120s
      timerRef.current = setTimeout(() => {
        setStatus(null);
        setAgentName("");
      }, 120000);
    };

    socket.on(`company-${companyId}-aiAgentStatus`, handleAiStatus);

    return () => {
      socket.off(`company-${companyId}-aiAgentStatus`, handleAiStatus);
      if (timerRef.current) clearTimeout(timerRef.current);
      socket.disconnect();
    };
  }, [ticketId, socketManager]);

  if (!status || !STATUS_MAP[status]) return null;

  const isDark = theme.palette.type === "dark";
  const dotColor = isDark ? "#7c9aff" : "#5B7CFF";
  const textColor = isDark ? "#93c5fd" : "#4a6ae8";

  return (
    <div className={classes.compact}>
      <div className={classes.dotsContainer}>
        <div className={classes.dot} style={{ backgroundColor: dotColor }} />
        <div className={classes.dot} style={{ backgroundColor: dotColor }} />
        <div className={classes.dot} style={{ backgroundColor: dotColor }} />
      </div>
      <span className={classes.text} style={{ color: textColor }}>
        {agentName} • {STATUS_MAP[status]}
      </span>
    </div>
  );
};

export default AiStatusIndicator;
